import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ARS,
  Cantidad,
  DECIMALES_DEL_EFECTIVO,
  Importe,
  MODO_DE_REDONDEO_DEL_EFECTIVO,
  Porcentaje,
  calcularLinea,
  calcularTotales,
  type LineaCalculada,
  descuentoPorCantidad,
  type Escala,
} from '@actualgold/shared';
import { pedir } from '../api/cliente';

/**
 * El estado de una venta en curso, compartido por las dos pantallas.
 *
 * Las dos venden lo mismo y de maneras muy distintas —el mostrador con el lector y el dedo, el
 * mayorista con el teclado— pero la venta es una sola. Duplicar el cálculo en cada pantalla
 * sería duplicar la regla, y dos reglas se separan en la primera corrección.
 *
 * El total se calcula acá con **la misma función que usa el servidor**, así que lo que la
 * persona ve antes de cobrar es exactamente lo que se va a cobrar.
 */

export interface VarianteEncontrada {
  readonly varianteId: string;
  readonly articuloId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly talle: string | null;
  readonly color: string | null;
  readonly esUnica: boolean;
  readonly decimales: number;
  readonly controlaExistencia: boolean;
  /** Viene con la búsqueda: un viaje por escaneo, no dos. */
  readonly precio: string | null;
}

export interface LineaDeVenta {
  readonly clave: string;
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly decimales: number;
  cantidad: string;
  /** Precio unitario tomado de la lista. Texto, nunca coma flotante. */
  precioUnitario: string;
  /** Bonificaciones en cascada, como las escribe el mayorista: "30 y 10". */
  descuentos: string[];
}

export type MedioDePago =
  | 'efectivo'
  | 'debito'
  | 'credito'
  | 'transferencia'
  /** No es plata: es la promesa de pagarla. Exige cliente identificado con cuenta. */
  | 'cuenta_corriente';

export interface ResultadoDeVenta {
  readonly comprobanteId: string;
  readonly numero: string;
  /** La letra que el servidor decidió. La pantalla la anticipa, pero la que vale es ésta. */
  readonly clase: string;
  /** Lo que va dentro del QR y abre el ticket sin sesión. */
  readonly tokenPublico: string;
  readonly subtotal: string;
  readonly descuentoTotal: string;
  readonly redondeo: string;
  readonly total: string;
}

function importeSeguro(texto: string): Importe {
  try {
    return Importe.desdeTexto(texto.replace(',', '.'), ARS);
  } catch {
    return Importe.cero(ARS);
  }
}

function cantidadSegura(texto: string): Cantidad {
  try {
    return Cantidad.desdeTexto(texto.replace(',', '.'));
  } catch {
    return Cantidad.CERO;
  }
}

function porcentajeSeguro(texto: string): Porcentaje {
  try {
    return Porcentaje.desdeTexto(texto.replace(',', '.'));
  } catch {
    return Porcentaje.CERO;
  }
}

/**
 * Una línea a medio escribir no puede romper la pantalla.
 *
 * Mientras se tipea "1," el texto no es un número válido, y una excepción ahí dejaría la venta
 * en blanco en medio del mostrador. Se toma como cero hasta que sea válido: el total se corrige
 * solo al terminar de escribir, y el servidor rechaza igual lo que no cierre.
 */
export function calcularLineaSegura(
  linea: LineaDeVenta,
  escalas: readonly Escala[] = [],
): LineaCalculada {
  // El tramo por cantidad de la lista entra como un descuento más, **con la misma función que usa
  // el servidor** (E36): lo que se ve antes de cobrar es lo que se cobra.
  const cantidad = cantidadSegura(linea.cantidad);
  const delTramo = descuentoPorCantidad(escalas, cantidad.aTexto());
  return calcularLinea(
    {
      cantidad,
      precioUnitario: importeSeguro(linea.precioUnitario),
      descuentos: [...linea.descuentos, ...(delTramo === null ? [] : [delTramo])].map(
        porcentajeSeguro,
      ),
    },
    MODO_DE_REDONDEO_DEL_EFECTIVO,
  );
}

/**
 * Una forma de pago de esta venta.
 *
 * `importe` vacío significa **«lo que falte»**, y no es un descuido: el caso normal es una sola
 * forma que cubre todo, y obligar a tipear el total ahí sería pedir que alguien copie a mano un
 * número que el sistema ya sabe. Cuando hay varias, se tipea lo que se cobró con las primeras y la
 * última se queda con el resto.
 */
export interface PagoDeVenta {
  readonly clave: string;
  readonly medioDePago: MedioDePago;
  readonly importe: string;
}

function pagoNuevo(medioDePago: MedioDePago): PagoDeVenta {
  return {
    clave: `${medioDePago}-${String(Date.now())}-${String(Math.random())}`,
    medioDePago,
    importe: '',
  };
}

export function useVenta(listaDePreciosId: string | undefined, escalas: readonly Escala[] = []) {
  const [lineas, setLineas] = useState<LineaDeVenta[]>([]);

  // Si la lista cambia con la venta empezada —se eligió un cliente con su propia lista, o un tipo
  // que fija otra—, los precios de lo ya cargado se vuelven a buscar en la nueva (E36). El servidor
  // cobra con la lista igual; esto es para que la pantalla no muestre el precio de la anterior.
  useEffect(() => {
    if (listaDePreciosId === undefined) return;
    let vigente = true;
    void (async () => {
      const codigos = [...new Set(lineas.map((linea) => linea.codigo))];
      const precios = new Map<string, string | null>();
      for (const codigo of codigos) {
        const encontradas = await pedir<VarianteEncontrada[]>(
          `/articulos/buscar?texto=${encodeURIComponent(codigo)}&listaDePreciosId=${listaDePreciosId}`,
        );
        for (const variante of encontradas) precios.set(variante.varianteId, variante.precio);
      }
      if (!vigente) return;
      setLineas((previas) =>
        previas.map((linea) => {
          const precio = precios.get(linea.varianteId);
          return precio === undefined || precio === null
            ? linea
            : { ...linea, precioUnitario: precio };
        }),
      );
    })();
    return () => {
      vigente = false;
    };
    // Sólo cuando cambia la lista: re-buscar con cada línea nueva pisaría lo que se acaba de cargar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaDePreciosId]);
  const [pagos, setPagos] = useState<readonly PagoDeVenta[]>([pagoNuevo('efectivo')]);
  const [entregado, setEntregado] = useState('');
  const [emitiendo, setEmitiendo] = useState(false);

  // **El mismo criterio que el servidor**: hay efectivo si *alguna* de las formas es efectivo. El
  // redondeo del efectivo depende de esto, así que si los dos no coincidieran el total de la
  // pantalla y el del comprobante serían distintos, que es la peor forma de estar roto.
  const hayEfectivo = pagos.some((pago) => pago.medioDePago === 'efectivo');

  const totales = useMemo(
    () =>
      calcularTotales(
        lineas.map((linea) => calcularLineaSegura(linea, escalas)),
        {
          moneda: Importe.cero(ARS),
          hayEfectivo,
          decimalesDelEfectivo: DECIMALES_DEL_EFECTIVO,
          modo: MODO_DE_REDONDEO_DEL_EFECTIVO,
        },
      ),
    [lineas, hayEfectivo, escalas],
  );

  /**
   * Cuánto va en cada forma de pago, ya repartido.
   *
   * La regla es una sola: **lo que se tipeó vale, y el resto va a la primera forma sin tipear**.
   * Con una sola forma —el caso de siempre— eso da el total entero sin que nadie escriba nada.
   */
  const repartidos = useMemo(() => {
    const tipeados = pagos.map((pago) =>
      pago.importe.trim() === '' ? null : importeSeguro(pago.importe),
    );
    const sumaDeTipeados = tipeados.reduce<Importe>(
      (suma, importe) => (importe === null ? suma : suma.sumar(importe)),
      Importe.cero(ARS),
    );
    const resto = totales.total.restar(sumaDeTipeados);
    const primeroSinTipear = tipeados.findIndex((importe) => importe === null);

    return pagos.map((pago, indice) => ({
      ...pago,
      // El resto puede ser negativo si alguien tipeó de más: se muestra como falta al revés, y el
      // servidor lo rechaza igual. No se recorta a cero, porque esconderlo sería mentir.
      calculado:
        tipeados[indice] ??
        (indice === primeroSinTipear && resto.esPositivo() ? resto : Importe.cero(ARS)),
    }));
  }, [pagos, totales.total]);

  /** Lo que falta cobrar. Cero es la venta cerrada; positivo es que falta plata. */
  const faltante = useMemo(
    () =>
      totales.total.restar(
        repartidos.reduce((suma, pago) => suma.sumar(pago.calculado), Importe.cero(ARS)),
      ),
    [repartidos, totales.total],
  );

  const agregarPago = useCallback((medioDePago: MedioDePago): void => {
    setPagos((previos) => [...previos, pagoNuevo(medioDePago)]);
  }, []);

  const cambiarPago = useCallback((clave: string, cambio: Partial<PagoDeVenta>): void => {
    setPagos((previos) =>
      previos.map((pago) => (pago.clave === clave ? { ...pago, ...cambio } : pago)),
    );
  }, []);

  const quitarPago = useCallback((clave: string): void => {
    setPagos((previos) => {
      // Nunca se queda sin ninguna: una venta sin forma de pago no se puede cobrar, y dejar la
      // lista vacía obligaría a agregar una para volver al estado normal.
      if (previos.length <= 1) return previos;
      const quedan = previos.filter((pago) => pago.clave !== clave);

      // **Al volver a una sola, el importe tipeado se borra.** Una forma sola significa «todo», y
      // sin esto quedaba pegado el número del reparto anterior: la pantalla mostraba los botones
      // de medio —que no tienen campo de importe—, decía «falta 44.606» y no había dónde
      // corregirlo. Lo encontró el guión de humo, no una lectura.
      return quedan.length === 1 && quedan[0] !== undefined
        ? [{ ...quedan[0], importe: '' }]
        : quedan;
    });
  }, []);

  /** El vuelto: lo que se entregó menos lo que hay que cobrar. Negativo significa que falta. */
  const vuelto = useMemo(
    () => importeSeguro(entregado).restar(totales.total),
    [entregado, totales.total],
  );

  const agregar = useCallback((variante: VarianteEncontrada, cantidad = '1'): void => {
    setLineas((previas) => {
      // Pasar el lector dos veces por el mismo código suma uno, no agrega una línea repetida:
      // es lo que espera cualquiera que haya trabajado en un mostrador.
      const existente = previas.findIndex(
        (linea) => linea.varianteId === variante.varianteId && linea.descuentos.length === 0,
      );
      if (existente >= 0 && cantidad === '1') {
        return previas.map((linea, indice) =>
          indice === existente
            ? {
                ...linea,
                cantidad: cantidadSegura(linea.cantidad).sumar(Cantidad.desdeTexto('1')).aTexto(),
              }
            : linea,
        );
      }

      return [
        ...previas,
        {
          clave: `${variante.varianteId}-${String(previas.length)}-${String(Date.now())}`,
          varianteId: variante.varianteId,
          codigo: variante.codigo,
          nombre:
            variante.esUnica || (variante.talle === null && variante.color === null)
              ? variante.nombre
              : `${variante.nombre} ${[variante.talle, variante.color].filter((p) => p !== null).join(' ')}`,
          decimales: variante.decimales,
          cantidad,
          precioUnitario: variante.precio ?? '0',
          descuentos: [],
        },
      ];
    });
  }, []);

  const cambiar = useCallback((clave: string, cambio: Partial<LineaDeVenta>): void => {
    setLineas((previas) =>
      previas.map((linea) => (linea.clave === clave ? { ...linea, ...cambio } : linea)),
    );
  }, []);

  const quitar = useCallback((clave: string): void => {
    setLineas((previas) => previas.filter((linea) => linea.clave !== clave));
  }, []);

  const limpiar = useCallback((): void => {
    setLineas([]);
    setEntregado('');
    // Las formas de pago vuelven a una sola: la venta que sigue es de otro cliente, y arrastrarle
    // el reparto de la anterior es la forma más fácil de cobrarle mal.
    setPagos([pagoNuevo('efectivo')]);
  }, []);

  const emitir = useCallback(
    async (datos: {
      readonly puntoDeVentaId: string;
      readonly puestoDeCajaId: string;
      /** A quién se le vende. Sin esto es el consumidor final que no dio datos. */
      readonly terceroId?: string | undefined;
      /** Deja pasar una venta que se pasa del límite. Exige permiso, y queda auditado. */
      readonly autorizarExcesoDeCredito?: boolean | undefined;
      /** Con qué tipo se emite. Sin esto, el que el comercio tenga marcado para vender. */
      readonly tipoId?: string | undefined;
      /** Si el tipo elegido cobra. En falso el documento se emite sin cobros. */
      readonly cobra?: boolean | undefined;
    }): Promise<ResultadoDeVenta> => {
      setEmitiendo(true);
      try {
        return await pedir<ResultadoDeVenta>('/ventas', {
          method: 'POST',
          headers: {
            // La clave la genera la pantalla y viaja igual en el reintento: es lo que
            // distingue "se cortó la red" de "entró otro cliente y compró lo mismo".
            'Idempotency-Key': globalThis.crypto.randomUUID(),
          },
          cuerpo: {
            puntoDeVentaId: datos.puntoDeVentaId,
            puestoDeCajaId: datos.puestoDeCajaId,
            ...(datos.terceroId === undefined ? {} : { terceroId: datos.terceroId }),
            ...(datos.autorizarExcesoDeCredito === true ? { autorizarExcesoDeCredito: true } : {}),
            ...(listaDePreciosId === undefined ? {} : { listaDePreciosId }),
            lineas: lineas.map((linea) => ({
              varianteId: linea.varianteId,
              cantidad: cantidadSegura(linea.cantidad).aTexto(),
              ...(linea.descuentos.length === 0
                ? {}
                : {
                    descuentos: linea.descuentos.map((valor) => porcentajeSeguro(valor).aTexto()),
                  }),
            })),
            ...(datos.tipoId === undefined ? {} : { tipoId: datos.tipoId }),
            // **Un documento que no cobra va sin cobros.** Mandarle uno es un error del servidor
            // —quien manda un cobro cree que cobró—, así que la pantalla no lo manda.
            cobros:
              datos.cobra === false
                ? []
                : repartidos
                    // Una forma en cero no se manda: sería una fila de cobro que no cobró nada.
                    .filter((pago) => !pago.calculado.esCero())
                    .map((pago) => ({
                      medioDePago: pago.medioDePago,
                      importe: pago.calculado.aTexto(),
                    })),
          },
        });
      } finally {
        setEmitiendo(false);
      }
    },
    [lineas, listaDePreciosId, repartidos],
  );

  return {
    lineas,
    totales,
    vuelto,
    pagos: repartidos,
    faltante,
    agregarPago,
    cambiarPago,
    quitarPago,
    /** La forma de pago cuando hay una sola. Es lo que mira el camino rápido del mostrador. */
    medioDePago: pagos.length === 1 ? (pagos[0]?.medioDePago ?? 'efectivo') : null,
    setMedioDePago: (medio: MedioDePago) => {
      setPagos([pagoNuevo(medio)]);
    },
    entregado,
    setEntregado,
    emitiendo,
    agregar,
    cambiar,
    quitar,
    limpiar,
    emitir,
  };
}
